import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Card,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/Images/Delete.svg";
import { ReactComponent as EditIcon } from "../../../assets/Images/EditIcon.svg";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import BaseButton from "../Common/Buttons/BaseButton";
import CustomScrollbars from "../CommonComponents/CustomScrollbars";
import ConfirmationPopup from "../CommonComponents/PopUpCards/ConfirmationPopup";
import { defaultInsightFields } from "./Constants";

const Insights = ({ workflowInsights, state, setState, handleSave }) => {
  const { questions, errMsg, insights = [], loading } = state;
  const [editingIndex, setEditingIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const handleInsightsSave = () => {
    const hasEmpty = questions.some((q) => !q.name.trim());
    if (hasEmpty) {
      setState((prev) => ({
        ...prev,
        errMsg: "Please fill all fields before saving",
      }));
      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          errMsg: "",
        }));
      }, 3000);
      return;
    }
    let updatedInsights: any = [];

    if (editingIndex !== null) {
      updatedInsights = [...insights];
      updatedInsights[editingIndex] = questions[0];
      handleSave(updatedInsights, true);
    } else {
      const existingNames = new Set(insights.map((i) => i.name));
      updatedInsights = [
        ...questions.filter((insight) => !existingNames.has(insight.name)),
        ...insights,
      ];
      handleSave(updatedInsights, false);
    }
    setTimeout(() => setEditingIndex(null), 1000);
  };

  const handleChange = (e, idx, field) => {
    const updated = [...questions];
    updated[idx][field] =
      field === "name" ? e.target.value.replace(/\s+/g, "") : e.target.value;
    setState((prev) => ({
      ...prev,
      questions: updated,
    }));
  };

  const addMoreColumn = (idx) => {
    if (questions[idx]?.name) {
      const add = { name: "", type: "" };
      setState((prev) => ({
        ...prev,
        questions: [add, ...prev.questions],
      }));
    } else {
      setState((prev) => ({
        ...prev,
        errMsg: "Please select the name first",
      }));
      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          errMsg: "",
        }));
      }, 3000);
    }
  };

  const removeQuestion = (idx) => {
    setState((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));
  };

  const handleEdit = (item, idx) => {
    setEditingIndex(idx);
    setState((prev) => ({
      ...prev,
      questions: [item],
    }));
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setState((prev) => ({
      ...prev,
      questions: [{ name: "", type: "" }],
    }));
  };

  const confirmDelete = (idx) => {
    setDeleteIndex(idx);
    setShowDeletePopup(true);
  };

  const handleDelete = () => {
    if (deleteIndex !== null) {
      const updatedInsights = insights.filter((_, i) => i !== deleteIndex);
      handleSave(updatedInsights, false, true);
      setDeleteIndex(null);
      setShowDeletePopup(false);
    }
  };

  return (
    <CustomScrollbars height="70vh">
      <Box sx={{ gap: 3, my: 1, pb: 2 }}>
        {questions.map((data, idx) => (
          <Box className="my-2" key={idx}>
            <Box className="flex-between-only">
              <Box flex={1}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Box className="flex-column" flex={1}>
                    <Typography className="font-14-grey my-1">
                      Insight Name
                    </Typography>
                    <Select
                      displayEmpty
                      value={data.name || ""}
                      onChange={(e) => handleChange(e, idx, "name")}
                      sx={commonStyles.typeSelect}
                      disabled={editingIndex !== null}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 200, overflowY: "auto" },
                        },
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Insight name
                      </MenuItem>
                      {workflowInsights
                        ?.filter(
                          (insight) =>
                            insight === data.name ||
                            (!questions.some(
                              (q, qIdx) => q.name === insight && qIdx !== idx
                            ) &&
                              !insights.some((i) => i.name === insight))
                        )
                        .map((insight, key) => (
                          <MenuItem value={insight} key={key}>
                            {insight}
                          </MenuItem>
                        ))}
                    </Select>
                  </Box>
                  <Box className="flex-column" flex={1}>
                    <Typography className="font-14-grey my-1">
                      Insight Type
                    </Typography>
                    <Select
                      displayEmpty
                      value={data.type || "custom"}
                      onChange={(e) => handleChange(e, idx, "type")}
                      sx={commonStyles.typeSelect}
                      MenuProps={{
                        PaperProps: {
                          style: { maxHeight: 200, overflowY: "auto" },
                        },
                      }}
                    >
                      <MenuItem value="custom">Custom</MenuItem>
                      <MenuItem value="default">Default</MenuItem>
                    </Select>
                  </Box>
                </Box>
              </Box>
              {!editingIndex && idx !== 0 && (
                <Box className="p-2">
                  <IconButton
                    disabled={questions.length === 1}
                    onClick={() => removeQuestion(idx)}
                    sx={{ marginTop: 3.5 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Box>

            {idx === 0 && (
              <>
                {errMsg && <span className="font-600-red py-1">{errMsg}</span>}
                {!editingIndex && (
                  <Box className="flex-start" sx={{ pt: 1 }}>
                    <Button
                      onClick={() => addMoreColumn(idx)}
                      variant="outlined"
                      startIcon={<AddIcon sx={{ color: "#666" }} />}
                      sx={commonStyles.addBtn}
                    >
                      Add
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        ))}
      </Box>

      <Box className="flex-end" sx={{ gap: 2 }}>
        {editingIndex !== null && (
          <BaseButton
            title="Cancel"
            variant="outlined"
            handleClick={handleCancelEdit}
          />
        )}
        <BaseButton
          title={editingIndex !== null ? "Update" : "Save"}
          variant="contained"
          loading={loading}
          handleClick={handleInsightsSave}
        />
      </Box>

      {insights.length > 0 && (
        <>
          <Typography className="font-14-grey" sx={{ my: 1 }}>
            Existing Insights
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 1.5,
            }}
          >
            {insights.map((item, idx) => (
              <Card
                key={idx}
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minWidth: 0,
                  boxShadow: 0,
                  borderRadius: 2,
                }}
              >
                <Typography
                  fontSize={14}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flexGrow: 1,
                  }}
                >
                  {item?.name}
                </Typography>
                {!defaultInsightFields.some(
                  (df) => df?.name === item?.name
                ) && (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(item, idx)}
                      disabled={editingIndex !== null}
                      sx={{ ml: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => confirmDelete(idx)}
                      disabled={editingIndex !== null}
                      sx={{ ml: 0.5 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Card>
            ))}
          </Box>
          <ConfirmationPopup
            show={showDeletePopup}
            toggle={() => setShowDeletePopup(false)}
            handleSave={handleDelete}
            message="Are you sure you want to delete this insight?"
            heading="Delete Insight"
            Icon={ConfirmationIcon}
            confirmText="Delete"
            cancelText="Cancel"
          />
        </>
      )}
    </CustomScrollbars>
  );
};

export default Insights;
